import type { ViewRecord, ViewVisibility } from "@/shared/domain/views";

/**
 * The two lists behind the picker's Personal and Team tabs, plus `unknown` for
 * Views whose visibility was not reported. Kept separate so the caller folds it
 * in visibly rather than through a `??` buried here.
 */
export interface PartitionedViews {
  personal: ViewRecord[];
  team: ViewRecord[];
  unknown: ViewRecord[];
}

/**
 * Split Views by who can see them.
 *
 * An absent visibility is deliberately not treated as private: that would show
 * a shared View under Personal. Callers fold `unknown` into Team, the safer
 * reading. Input order is preserved within each list.
 */
export function partitionViewsByVisibility(
  views: readonly ViewRecord[],
): PartitionedViews {
  const partitioned: PartitionedViews = {
    personal: [],
    team: [],
    unknown: [],
  };

  for (const view of views) {
    bucketFor(view.visibility, partitioned).push(view);
  }

  return partitioned;
}

function bucketFor(
  visibility: ViewVisibility | undefined,
  partitioned: PartitionedViews,
): ViewRecord[] {
  switch (visibility) {
    case "private":
      return partitioned.personal;
    case "organization":
      return partitioned.team;
    default:
      return partitioned.unknown;
  }
}
